import {Flag, Tabs, type TabProps} from 'platform/components';
import {Box, Grid, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import type {MonthlyStockTakeDetailResponse} from '@dms/api/reporting';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useMonthlyStockTakeDetailView} from '../hooks/useMonthlyStockTakeDetailView';
import {DailyStockTakeSummaryTile} from './DailyStockTakeSummaryTile';
import {MonthlyStockTakeSection} from './MonthlyStockTakeSection';
import {StockTakeReportButton} from './StockTakeReportButton';

const SUMMARY_TAB = 'summary';
const COMPLETED_TAB = 'completed';
const IN_PROGRESS_TAB = 'inProgress';
const HEALTH_TAB = 'health';
const INVENTORY_TAB = 'inventory';
const TAB_CONTENT_MIN_HEIGHT_STEPS = 96;

interface MonthlyStockTakeDetailContentProps extends RequiredTestIdProps {
  detail: NonNullable<MonthlyStockTakeDetailResponse>;
  currency: string | Nullish;
}

export function MonthlyStockTakeDetailContent(props: MonthlyStockTakeDetailContentProps) {
  const [activeTab, setActiveTab] = useState(SUMMARY_TAB);
  const {
    check,
    date,
    money,
    sections,
    summaryMetrics,
    monthlyActivityMetrics,
    validationMetrics,
    comparisonMetrics,
    completedReceiptMetrics,
    completedIssueMetrics,
    inProgressMetrics,
    pendingReliabilityMetrics,
    healthMetrics,
    slowStockMetrics,
    slowStockCountMetrics,
    ytdMetrics,
    inventoryMetrics,
    writeOffMetrics,
  } = useMonthlyStockTakeDetailView(props);

  const tabs: TabProps[] = [
    {
      id: SUMMARY_TAB,
      title: i18n.t('general.labels.summary'),
      content: (
        <VStack spacing={2}>
          <section role="group" aria-label={i18n.t('entity.warehouse.reporting.inventoryValues')}>
            <Grid columns={[2, 4]} spacing={2}>
              {summaryMetrics.map((metric) => (
                <section key={metric.id} role="group" aria-label={metric.label}>
                  <DailyStockTakeSummaryTile
                    label={metric.label}
                    tooltip={metric.tooltip}
                    value={metric.value}
                    note={metric.note}
                    isHighlighted={metric.id === 'inventoryValueDifference'}
                    data-testid={suffixTestId(metric.id, props)}
                  />
                </section>
              ))}
            </Grid>
          </section>
          <Grid columns={[1, 3]} spacing={2}>
            <MonthlyStockTakeSection
              title={i18n.t('entity.warehouse.reporting.monthlyActivity')}
              metrics={monthlyActivityMetrics}
              data-testid={suffixTestId('monthlyActivity', props)}
            />
            <MonthlyStockTakeSection
              title={i18n.t('entity.warehouse.reporting.validationAndContinuity')}
              metrics={validationMetrics}
              data-testid={suffixTestId('validation', props)}
            />
            <MonthlyStockTakeSection
              title={i18n.t('entity.warehouse.reporting.changeFromPreviousMonth')}
              metrics={comparisonMetrics}
              data-testid={suffixTestId('comparison', props)}
            />
          </Grid>
        </VStack>
      ),
    },
    {
      id: COMPLETED_TAB,
      title: i18n.t('entity.warehouse.reporting.completedMovements'),
      content: (
        <Grid columns={[1, 3]} spacing={2}>
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.receiptsBreakdown')}
            metrics={completedReceiptMetrics}
            colorStripe="green"
            data-testid={suffixTestId('completedReceipts', props)}
          />
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.issuesBreakdown')}
            metrics={completedIssueMetrics}
            colorStripe="red"
            data-testid={suffixTestId('completedIssues', props)}
          />
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.monthlyActivity')}
            metrics={monthlyActivityMetrics}
            data-testid={suffixTestId('completedTotals', props)}
          />
        </Grid>
      ),
    },
    {
      id: IN_PROGRESS_TAB,
      title: i18n.t('entity.warehouse.reporting.inProgressMovements'),
      content: (
        <Grid columns={[1, 2]} spacing={2}>
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.inProgressMovements')}
            metrics={inProgressMetrics}
            data-testid={suffixTestId('inProgress', props)}
          />
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.pendingSnapshotReliability')}
            metrics={pendingReliabilityMetrics}
            data-testid={suffixTestId('pendingReliability', props)}
          />
        </Grid>
      ),
    },
    {
      id: HEALTH_TAB,
      title: i18n.t('entity.warehouse.reporting.inventoryHealth'),
      content: (
        <Grid columns={[1, 3]} spacing={2}>
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.inventoryHealth')}
            metrics={healthMetrics}
            data-testid={suffixTestId('health', props)}
          />
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.slowStock')}
            metrics={slowStockMetrics}
            data-testid={suffixTestId('slowStock', props)}
          />
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.cardsWithValue')}
            metrics={slowStockCountMetrics}
            data-testid={suffixTestId('slowStockCounts', props)}
          />
        </Grid>
      ),
    },
    {
      id: INVENTORY_TAB,
      title: i18n.t('entity.warehouse.reporting.inventoryAndYtd'),
      content: (
        <Grid columns={[1, 3]} spacing={2}>
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.yearToDate')}
            metrics={ytdMetrics}
            data-testid={suffixTestId('ytd', props)}
          />
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.inventoryAdjustments')}
            metrics={inventoryMetrics}
            data-testid={suffixTestId('inventory', props)}
          />
          <MonthlyStockTakeSection
            title={i18n.t('entity.warehouse.reporting.writeOffs')}
            metrics={writeOffMetrics}
            data-testid={suffixTestId('writeOffs', props)}
          />
        </Grid>
      ),
    },
  ];

  return (
    <VStack spacing={2}>
      <HStack spacing={3} align="center" justify="space-between" wrap>
        <HStack spacing={3} align="center" wrap>
          <Text size="large" alternative data-testid={suffixTestId('warehouse', props)}>
            {`${i18n.t('entity.warehouse.labels.warehouse')}: ${
              props.detail.warehouseName ?? props.detail.warehouseId ?? EMPTY_PLACEHOLDER
            }`}
          </Text>
          <Text size="small" color="secondary" data-testid={suffixTestId('branchAndMonth', props)}>
            {`${i18n.t('entity.warehouse.labels.branch')}: ${
              props.detail.branchName ?? props.detail.branchId ?? EMPTY_PLACEHOLDER
            } · ${date(props.detail.closingMonth)}`}
          </Text>
          <Show when={check}>
            {(check) => (
              <Flag
                isSubtle
                colorScheme={check.colorScheme}
                label={check.label}
                data-testid={suffixTestId('checkStatus', props)}
              />
            )}
          </Show>
        </HStack>
        <StockTakeReportButton
          report={{
            kind: 'monthly',
            filename: `monthly-inventory-closing-${encodeURIComponent(props.detail.id ?? props.detail.closingMonth ?? 'report')}.pdf`,
            title: i18n.t('page.warehouse.labels.monthlyInventoryClosing'),
            metadata: [
              {
                id: 'warehouse',
                label: i18n.t('entity.warehouse.labels.warehouse'),
                value: props.detail.warehouseName ?? props.detail.warehouseId ?? EMPTY_PLACEHOLDER,
              },
              {
                id: 'branch',
                label: i18n.t('entity.warehouse.labels.branch'),
                value: props.detail.branchName ?? props.detail.branchId ?? EMPTY_PLACEHOLDER,
              },
              {
                id: 'month',
                label: i18n.t('general.labels.date'),
                value: date(props.detail.closingMonth),
              },
            ],
            sections,
          }}
        />
      </HStack>

      <HStack spacing={4} wrap>
        {monthlyActivityMetrics.map((metric) => (
          <Text key={metric.id} size="xSmall" color="secondary">
            {`${metric.label}: ${metric.value}`}
          </Text>
        ))}
        <Text size="xSmall" color="secondary">
          {`${i18n.t('entity.warehouse.reporting.checkTotal')}: ${money(props.detail.checkSum)}`}
        </Text>
      </HStack>

      <Box minHeight={TAB_CONTENT_MIN_HEIGHT_STEPS}>
        <Tabs
          tabs={tabs}
          activeTabId={activeTab}
          onChange={setActiveTab}
          data-testid={suffixTestId('tabs', props)}
        />
      </Box>
    </VStack>
  );
}
